import {
  FiArchive,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiGrid,
  FiList,
  FiSettings,
  FiSidebar,
} from "react-icons/fi";
import { boardPublicPath } from "@/config/browserBoard";
import { ArchiveSearchBar } from "@/features/archive/components/ArchiveSearchBar";
import type {
  ArchiveEvent,
  ArchiveEventNotifier,
} from "@/features/archive/mediators/archiveEvents";
import type { SearchScope } from "@/features/search/searchScope";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { useComponentNode } from "@/shared/ui/component-hierarchy/hooks";
import { DesktopRefreshLabel } from "@/shared/ui/navigation/DesktopRefreshLabel";

type DesktopArchiveToolbarProps = {
  readonly isFetching: boolean;
  readonly searchQuery?: string;
  readonly searchScope?: SearchScope;
};

export type DesktopArchiveToolbarViewProps = {
  readonly isFetching: boolean;
  readonly onEvent: ArchiveEventNotifier;
  readonly searchQuery: string;
  readonly searchScope: SearchScope;
};

/** 親へイベントを透過する過去ログツールバーのPresenter。 */
export function DesktopArchiveToolbar({
  isFetching,
  searchQuery = "",
  searchScope = "all",
}: DesktopArchiveToolbarProps): React.ReactElement {
  const node = useComponentNode();
  const notify: ArchiveEventNotifier = (
    event: ArchiveEvent,
  ): ReturnType<ArchiveEventNotifier> => node.dispatch(event).status;

  return (
    <ComponentScope node={node}>
      <DesktopArchiveToolbarView
        isFetching={isFetching}
        searchQuery={searchQuery}
        searchScope={searchScope}
        onEvent={notify}
      />
    </ComponentScope>
  );
}

/** カタログと同じ外観をpropsだけから描画する過去ログView。 */
export function DesktopArchiveToolbarView({
  isFetching,
  searchQuery,
  searchScope,
  onEvent,
}: DesktopArchiveToolbarViewProps): React.ReactElement {
  return (
    <nav
      className="desktop-catalog-toolbar"
      aria-label="過去ログナビゲーション"
    >
      <fieldset
        className="desktop-catalog-toolbar-navigation"
        aria-label="ページ内移動"
      >
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={(): void => {
            onEvent({ type: "archive/sidebar-toggle-requested" });
          }}
          aria-label="履歴サイドバーを切り替える"
        >
          <FiSidebar aria-hidden="true" />
        </button>
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={(): void => {
            onEvent({ type: "archive/scroll-requested", edge: "top" });
          }}
          aria-label="ページ上部へ移動"
        >
          <FiChevronUp aria-hidden="true" />
        </button>
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={(): void => {
            onEvent({ type: "archive/scroll-requested", edge: "bottom" });
          }}
          aria-label="ページ下部へ移動"
        >
          <FiChevronDown aria-hidden="true" />
        </button>
      </fieldset>

      <fieldset
        className="desktop-catalog-toolbar-primary"
        aria-label="カタログ表示"
      >
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/board")}
        >
          <FiList aria-hidden="true" />
          リスト
        </a>
        <a className="desktop-catalog-toolbar-link" href={boardPublicPath("/")}>
          <FiGrid aria-hidden="true" />
          カタログ
        </a>
      </fieldset>

      <fieldset
        className="desktop-catalog-toolbar-secondary"
        aria-label="過去ログ操作"
      >
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/history/viewed")}
        >
          <FiClock aria-hidden="true" />
          履歴
        </a>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/archive")}
          aria-current="page"
        >
          <FiArchive aria-hidden="true" />
          過去ログ
        </a>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/settings")}
          onClick={(event: React.MouseEvent<HTMLAnchorElement>): void => {
            const status = onEvent({
              type: "archive/settings-link-clicked",
              hasModifier: hasLinkModifierKey(event),
            });
            if (status === "handled") event.preventDefault();
          }}
        >
          <FiSettings aria-hidden="true" />
          設定
        </a>
        <button
          className="desktop-catalog-toolbar-action desktop-catalog-toolbar-refresh"
          type="button"
          disabled={isFetching}
          onClick={(): void => {
            onEvent({ type: "archive/refresh-requested" });
          }}
        >
          <DesktopRefreshLabel isFetching={isFetching} />
        </button>
      </fieldset>

      <ArchiveSearchBar
        className="desktop-catalog-toolbar-search"
        isFetching={isFetching}
        query={searchQuery}
        scope={searchScope}
      />
    </nav>
  );
}
